import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import ServerStatus from './ServerStatus';
import Logo from '../../../public/assets/images/logo.svg';
const Sidebar = ({ isOpen = true, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // try to restore last-selected Provider from localStorage (if present)
  const [selectedProvider, setSelectedProvider] = useState(() => {
    try {
      return localStorage.getItem('@selectedProvider') || 'main-provider';
    } catch {
      return 'main-provider';
    }
  });

  const [providerOptions, setProviderOptions] = useState([]);

  const mockProviderOptions = [
    {
      value: 'main-provider',
      label: 'خدمات دهنده اصلی',
      address: 'تهران، خیابان ولیعصر',
      status: 'active',
      employeeCount: 8,
    },
    {
      value: 'branch-1',
      label: 'شعبه شمال',
      address: 'تهران، خیابان شریعتی',
      status: 'active',
      employeeCount: 5,
    },
    {
      value: 'branch-2',
      label: 'شعبه غرب',
      address: 'تهران، خیابان ستاری',
      status: 'maintenance',
      employeeCount: 6,
    },
  ];

  useEffect(() => {
    // load providers from localStorage and normalize; fall back to mock
    const raw = (() => {
      try {
        return localStorage.getItem('@providers');
      } catch (e) {
        console.warn('[Sidebar] localStorage access failed', e);
        return null;
      }
    })();

    console.debug('[Sidebar] raw @providers:', raw);

    let parsed = null;
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch (err) {
        console.warn(
          '[Sidebar] Failed to parse @providers JSON, will use mock',
          err,
        );
      }
    }

    if (Array.isArray(parsed) && parsed.length > 0) {
      // normalize items so we always have value/label/address
      const normalized = parsed.map((s, idx) => ({
        value: s?.value ?? s?.id ?? `provider-${idx}`,
        label: s?.label ?? s?.name ?? 'بدون نام',
        address: s?.address ?? s?.location ?? '',
        status: s?.status ?? 'active',
        employeeCount: s?.employeeCount ?? s?.employees ?? 0,
        ...s,
      }));

      setProviderOptions(normalized);

      // ensure the providers is valid; otherwise pick a good default
      setSelectedProvider((prev) => {
        const exists = normalized.find((x) => x.value === prev);
        if (exists) return prev;
        const main = normalized.find((x) => x.value === 'main-providers');
        return main ? main.value : normalized[0].value;
      });
    } else {
      setProviderOptions(mockProviderOptions);

      setSelectedProvider((prev) => {
        const exists = mockProviderOptions.find((x) => x.value === prev);
        return exists ? prev : mockProviderOptions[0].value;
      });
    }
  }, []);

  // persist selection so next load restores it
  useEffect(() => {
    try {
      if (selectedProvider)
        localStorage.setItem('@selectedProvider', selectedProvider);
    } catch (e) {
      console.warn('[Sidebar] could not persist @selectedProvider', e);
    }
  }, [selectedProvider]);

  const navigationItems = [
    {
      label: 'داشبورد',
      path: '/provider-dashboard',
      icon: 'LayoutDashboard',
      description: 'نمای کلی و آمار خدمات دهنده‌ها',
    },
    // {
    //   label: 'مدیریت تیم',
    //   path: '/provider-management',
    //   icon: 'Store',
    //   description: 'مدیریت اطلاعات و تنظیمات تیم',
    //   // subItems: [
    //   //   {
    //   //     label: 'مدیریت کارکنان',
    //   //     path: '/employee-management',
    //   //     icon: 'Users',
    //   //   },
    //   //   {
    //   //     label: 'مدیریت خدمات',
    //   //     path: '/service-management',
    //   //     icon: 'Scissors',
    //   //   },
    //   //   {
    //   //     label: 'تقویم دسترسی',
    //   //     path: '/availability-calendar',
    //   //     icon: 'Calendar',
    //   //   },
    //   // ],
    // },

    // {
    //   label: 'پروفایل کاربری',
    //   path: '/user-profile',
    //   icon: 'User',
    //   description: 'مدیریت حساب کاربری',
    // },
    {
      label: 'غربالگری و تست‌ها',
      path: '/assessments',
      icon: 'ClipboardList',
    },
    {
      label: 'مدیریت هوش مصنوعی',
      path: '/ai-management',
      icon: 'Bot', // یا Brain
    },
    {
      label: 'ارجاع‌های سینوهه',
      path: '/sinoheh-referrals',
      icon: 'ClipboardCheck',
      description: 'مدیریت ارجاع‌های ثبت‌شده سینوهه',
    },
    {
      label: 'لیست جامع مشتریان',
      path: '/all-clients',
      icon: 'Users', // یا آیکون مناسب دیگر
    },
    {
      label: 'خروج',
      path: '/',
      icon: 'LogOut',
      function: () => {
        localStorage.removeItem('token');
        navigate('/');
      },
      description: 'خروج از حساب کاربری',
    },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) onClose?.();
  };

  const handleProviderChange = (providerValue) => {
    setSelectedProvider(providerValue);
  };

  const isActiveRoute = (path) => location?.pathname === path;

  const isParentActive = (item) => {
    if (item?.subItems) {
      return (
        item?.subItems?.some(
          (subItem) => location?.pathname === subItem?.path,
        ) || location?.pathname === item?.path
      );
    }
    return location?.pathname === item?.path;
  };

  const selectedProviderData = providerOptions?.find(
    (provider) => provider?.value === selectedProvider,
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success';
      case 'maintenance':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'فعال';
      case 'maintenance':
        return 'تعمیرات';
      default:
        return 'نامشخص';
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-modal z-mobile-nav lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
        fixed top-0 right-0 h-full w-60 bg-card border-l border-border z-mobile-nav lg:z-sidebar
        transform transition-transform duration-200 ease-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* Header with logo */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-32 h-16 rounded-lg flex items-center justify-center p-2">
                {/* <Icon name="Scissors" size={20} color="white" /> */}
                <img
                  src={Logo}
                  alt="Logo"
                  //className="w-20 h-20 mx-auto mb-4 rounded-full"
                />
              </div>
              {/* <div>
                <h1 className="text-lg font-semibold text-foreground">
                  Nails by Marjan
                </h1>
                <p className="text-xs text-muted-foreground">پنل مدیریت</p>
              </div> */}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="lg:hidden"
              iconName="X"
              iconSize={20}>
              <span className="sr-only">Close sidebar</span>
            </Button>
          </div>

          {/* Salon context switcher */}
          {/* <div className="p-4 border-b border-border">
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">
                خدمات دهنده فعال
              </label>
              <div className="relative">
                <select
                  value={selectedSalon}
                  onChange={(e) => handleSalonChange(e?.target?.value)}
                  className="w-full p-2 text-sm bg-muted border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent appearance-none">
                  {salonOptions?.map((salon) => (
                    <option key={salon?.value} value={salon?.value}>
                      {salon?.label}
                    </option>
                  ))}
                </select>

                <Icon
                  name="ChevronDown"
                  size={16}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none"
                />
              </div>

              {selectedSalonData && (
                <p className="text-xs text-muted-foreground">
                  {selectedSalonData?.address}
                </p>
              )}
            </div>
          </div> */}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigationItems?.map((item) => (
              <div key={item?.path}>
                <button
                  onClick={() => handleNavigation(item?.path)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-right transition-colors duration-150
                    ${
                      isParentActive(item)
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-foreground hover:bg-muted'
                    }
                  `}>
                  <Icon
                    name={item?.icon}
                    size={20}
                    color={
                      isParentActive(item) ? 'currentColor' : 'currentColor'
                    }
                  />
                  <div className="flex-1 text-right">
                    <div className="text-sm font-medium">{item?.label}</div>
                    {item?.description && (
                      <div className="text-xs opacity-75 mt-0.5">
                        {item?.description}
                      </div>
                    )}
                  </div>
                </button>

                {/* Sub-navigation */}
                {item?.subItems && isParentActive(item) && (
                  <div className="mt-2 mr-6 space-y-1 animate-fade-in">
                    {item?.subItems?.map((subItem) => (
                      <button
                        key={subItem?.path}
                        onClick={() =>
                          item.function
                            ? item.function()
                            : handleNavigation(subItem?.path)
                        }
                        className={`
                          w-full flex items-center gap-2 px-3 py-2 rounded-md text-right text-sm transition-colors duration-150
                          ${
                            isActiveRoute(subItem?.path)
                              ? 'bg-accent text-accent-foreground'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }
                        `}>
                        <Icon name={subItem?.icon} size={16} />
                        {subItem?.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-border">
            <div className="text-xs text-muted-foreground text-center">
              <p>نسخه 1.0.0</p>
              <p className="mt-1">© 2025 مدیریت متمرکز تیم</p>
            </div>
          </div>
          <ServerStatus />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
